import JSZip from 'jszip';

function getFileExtension(filename: string): string {
  const basename = filename.split('/').pop() || filename;

  // Handle hidden files like .env, .gitignore
  if (basename.startsWith('.') && !basename.slice(1).includes('.')) {
    return '';
  }

  // Handle files without extension like README, LICENSE
  if (!basename.includes('.')) {
    return '';
  }

  return basename.split('.').pop()?.toLowerCase() || '';
}

export async function getZipFileExtensions(
  arquivoZip: File | Blob,
): Promise<string[]> {
  const jszip = new JSZip();

  try {
    const zip = await jszip.loadAsync(arquivoZip);
    const extensions: Set<string> = new Set();

    for (const filename of Object.keys(zip.files)) {
      if (!zip.files[filename].dir) {
        const extension = getFileExtension(filename);
        if (extension) {
          extensions.add(extension);
        }
      }
    }
    return Array.from(extensions);
  } catch (_error) {
    console.error('Failed to read zip file', _error?.message);
    return [];
  }
}

export async function validateZipFileExtensions(
  arquivoZip: File | Blob,
  allowedExtensions: string[],
): Promise<{ valid: boolean; invalidFiles: string[] }> {
  const jszip = new JSZip();

  try {
    const zip = await jszip.loadAsync(arquivoZip);
    const invalidFiles: string[] = [];
    const normalizedAllowed = allowedExtensions.map((ext) => ext.toLowerCase());

    for (const filename of Object.keys(zip.files)) {
      if (!zip.files[filename].dir) {
        const extension = getFileExtension(filename);
        if (!normalizedAllowed.includes(extension)) {
          invalidFiles.push(filename);
        }
      }
    }

    return {
      valid: invalidFiles.length === 0,
      invalidFiles,
    };
  } catch (_error) {
    console.error('Failed to read zip file', _error?.message);
    return { valid: false, invalidFiles: [] };
  }
}

export async function validateRequiredExtensions(
  arquivoZip: File | Blob,
  requiredExtensions: string[],
): Promise<{
  valid: boolean;
  extensions: string[];
  missingExtensions: string[];
}> {
  const extensions = await getZipFileExtensions(arquivoZip);
  const normalizedRequired = requiredExtensions.map((ext) => ext.toLowerCase());

  const missingExtensions = normalizedRequired.filter(
    (ext) => !extensions.includes(ext),
  );

  return {
    valid: missingExtensions.length === 0,
    extensions,
    missingExtensions,
  };
}

export interface UnzipOptions {
  allowedExtensions?: string[];
  filterExtensions?: string[];
}

export async function unzipFiles(
  arquivoZip: File | Blob,
  options: UnzipOptions = {},
): Promise<Array<File>> {
  const jszip = new JSZip();
  const { allowedExtensions, filterExtensions } = options;

  const normalizedAllowed = allowedExtensions?.map((ext) => ext.toLowerCase());
  const normalizedFilter = filterExtensions?.map((ext) => ext.toLowerCase());

  try {
    const zip = await jszip.loadAsync(arquivoZip);
    const files: Array<File> = [];

    for (const filename of Object.keys(zip.files)) {
      if (zip.files[filename].dir) {
        continue;
      }

      const extension = getFileExtension(filename);

      if (normalizedFilter?.includes(extension)) {
        continue;
      }

      if (normalizedAllowed && !normalizedAllowed.includes(extension)) {
        continue;
      }

      try {
        const blobContent = await zip.files[filename].async('blob');
        files.push(
          new File([blobContent], filename, {
            type: blobContent.type,
          }),
        );
      } catch (_error) {
        console.log('Failed to unzip file ' + filename, _error);
      }
    }

    return files;
  } catch (_error) {
    console.error('Failed to unzip files', _error?.message);
    return [];
  }
}
